import { createSlice } from '@reduxjs/toolkit';

import { findOpportunities } from 'api/opportunityCuratorAPI';
import { handleError } from 'services/Error';
import { Grids } from 'store/grids/gridsSlice';

const initialPageData = {
    pages: {},
    rowCount: undefined,
};

export const opportunitiesSlice = createSlice({
    name: 'opportunities',
    initialState: {
        pageData: initialPageData,
    },
    reducers: {
        setPageData(state, action) {
            const { page, opportunities } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    pages: {
                        ...state.pageData.pages,
                        [page]: opportunities,
                    },
                },
            };
        },
        setAdditionalPageData(state, action) {
            const { rowCount } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    rowCount,
                },
            };
        },
        resetPageData(state, action) {
            const { page } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    pages: {
                        ...state.pageData.pages,
                        [page]: undefined,
                    },
                },
            };
        },
        resetAllPages(state) {
            return { ...state, pageData: initialPageData };
        },
    },
});

export const { setPageData, setAdditionalPageData, resetPageData, resetAllPages } =
    opportunitiesSlice.actions;

export const fetchOpportunities =
    (page, timeFrameFilter, searchText) => async (dispatch, getState) => {
        try {
            const { activeFilters, sort } = getState().grids[Grids.OPPORTUNITIES];

            // Only include filters w/ values selected
            // Exclude timeFrame filter, sent separately
            const filters = Object.fromEntries(
                Object.entries(activeFilters).filter(
                    ([key, val]) => val?.length > 0 && key !== 'timeFrame',
                ),
            );

            const sortKeyMap = {
                patientName: 'patient',
                patientDOB: 'patient_dob',
                memberId: 'member_id',
                authId: 'case_id',
                productServiceName: 'product_service_name',
                segmentName: 'segment_name',
                receivedDate: 'received_time',
            };

            const sorts = Object.fromEntries(
                sort
                    .map((obj) => {
                        const mappedKey = sortKeyMap[obj.id];
                        return mappedKey ? [mappedKey, obj.desc] : undefined;
                    })
                    .filter((item) => item != null),
            );

            const haveFilters = Object.keys(filters).length > 0;
            const haveSorts = Object.keys(sorts).length > 0;

            const filterBody =
                haveFilters || haveSorts
                    ? {
                          ...(haveFilters ? { filters } : {}),
                          ...(haveSorts ? { sorts } : {}),
                      }
                    : undefined;

            const response = await findOpportunities({
                page,
                searchTerm: searchText,
                dateFilter: timeFrameFilter,
                body: filterBody,
            });

            const { count, opportunities } = response;

            if (page === 0) {
                dispatch(
                    setAdditionalPageData({
                        rowCount: count,
                    }),
                );
            }

            dispatch(setPageData({ page, opportunities }));
        } catch (err) {
            handleError('An error occurred while retrieving opportunities.', err);
        }
    };
